import { useState } from "react";
import { track } from "@/lib/analytics";
import { absUrl, DEFAULT_SHARE_TEXT } from "@/lib/site";
import { cn } from "@/lib/utils";

interface ShareRowProps {
  text?: string;
  path?: string;
  /** Leave out the native-share button (it duplicates the others on desktop). */
  compact?: boolean;
  className?: string;
  /** More actions to set in the same line, after the share links. */
  extra?: React.ReactNode[];
}

/**
 * Sharing as one line of text links. The canonical URL is used on the server and in the
 * browser alike: building it from window.location gave the SSR HTML and the hydrated
 * page different hrefs (a hydration mismatch on every page), and on a preview deploy
 * it shared the preview's address instead of the real one.
 */
export function ShareRow({
  text = DEFAULT_SHARE_TEXT,
  path = "/",
  compact = false,
  className,
  extra = [],
}: ShareRowProps) {
  const [copied, setCopied] = useState(false);
  const url = absUrl(path);
  const enc = encodeURIComponent;

  const twitter = `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
  const email = `mailto:?subject=${enc("Worth knowing about ticks")}&body=${enc(`${text}\n\n${url}`)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      track("share_clicked", { method: "copy_link", path });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  const nativeShare = async () => {
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({ title: "LoneStarMap", text, url });
        track("share_clicked", { method: "native", path });
        return;
      }
    } catch {
      // user cancelled or share failed — fall through to copy
    }
    await copy();
  };

  const item =
    "font-medium text-foreground underline decoration-border underline-offset-4 transition-colors hover:decoration-current";

  return (
    <ul
      className={cn(
        "flex flex-wrap items-center gap-y-2 text-sm [&>li:not(:last-child)]:after:mx-2 [&>li:not(:last-child)]:after:text-muted-foreground [&>li:not(:last-child)]:after:content-['·']",
        className,
      )}
    >
      {!compact && (
        <li>
          <button type="button" onClick={nativeShare} className={item}>
            Share
          </button>
        </li>
      )}
      <li>
        <button type="button" onClick={copy} className={item} aria-live="polite">
          {copied ? "Link copied" : "Copy link"}
        </button>
      </li>
      <li>
        <a
          href={email}
          className={item}
          onClick={() => track("share_clicked", { method: "email", path })}
        >
          Email
        </a>
      </li>
      <li>
        <a
          href={facebook}
          target="_blank"
          rel="noopener noreferrer"
          className={item}
          aria-label="Share on Facebook (opens in a new tab)"
          onClick={() => track("share_clicked", { method: "facebook", path })}
        >
          Facebook
        </a>
      </li>
      <li>
        <a
          href={twitter}
          target="_blank"
          rel="noopener noreferrer"
          className={item}
          aria-label="Share on X (opens in a new tab)"
          onClick={() => track("share_clicked", { method: "x", path })}
        >
          X
        </a>
      </li>
      {extra.map((node, i) => (
        <li key={i}>{node}</li>
      ))}
    </ul>
  );
}
